import axios from 'axios'
import ImageConverter from './ImageConverter'

interface ClipSize {
  l: number
  t: number
  w: number
  h: number
}

interface CorrectImageSize {
  w: number
  h: number
}

interface CorrectMarkers {
  bl: { x: number; y: number }
  br: { x: number; y: number }
  tl: { x: number; y: number }
  tr: { x: number; y: number }
}

interface RotateQrPos {
  x: number
  y: number
}

/**
 * Base64形式の画像のURLをBase64形式の画像に変換します
 * @example
 *   data:image/png;base64,test -> test
 *   data:image/jpg;base64,test -> test
 *   data:image/jpeg;base64,test -> test
 * @param base64ImageUrl Base64形式の画像のURL
 * @returns Base64形式の画像
 */
function base64UrlToBase64(base64ImageUrl: string): string {
  return ImageConverter.base64url2base64(base64ImageUrl)
}

/**
 * キャンバス要素をBase64形式の画像に変換します
 * @param canvas キャンバス要素
 * @returns Base64形式の画像
 */
async function canvasToBase64(canvasEl: HTMLCanvasElement): Promise<string> {
  return base64UrlToBase64(await toBase64Url(canvasEl))
}

/**
 * Base64形式の画像を指定の大きさで切り抜きます
 * @param src HTMLキャンバス要素
 * @param size l: 左端(px)
 *             t: 上端(px)
 *             w: 左端からどれくらい切り抜くか(px)
 *             h: 上端からどれくらい切り抜くか(px)
 * @returns 切り抜き後のBase64形式の画像
 */
async function clip(
  src: HTMLCanvasElement | string,
  size: ClipSize
): Promise<string> {
  const imt64Element = await ImageConverter.toImage(src)
  const canvas = ImageConverter.crop_image2canvas(imt64Element, size)
  const base64 = await canvasToBase64(canvas)
  if (src instanceof HTMLCanvasElement) {
    disposeCanvas(src)
  }
  disposeCanvas(canvas)
  return base64
}

/**
 * BansheeへリクエストしてBase64形式の画像を補正します
 * @param base64Image Base64形式の画像
 * @param correctImageSize 補正後のサイズ
 * @param correctMarkers 補正マーカーの位置リスト
 * @param asGray グレースケールフラグ
 * @param rotateQrPos QRコードの位置
 * @returns result: 補正結果(ok/ng),
 *          image: 補正後のBase64形式の画像
 */
async function correctImage(
  base64Image: string,
  correctImageSize: CorrectImageSize,
  correctMarkers: CorrectMarkers,
  asGray: boolean,
  rotateQrPos: RotateQrPos | null,
  disableWhitebarance: boolean
): Promise<{
  result: string
  image: string
  auto_rotate_qr: boolean
}> {
  const params: {
    image: string
    correct_image_size: CorrectImageSize
    correct_markers: CorrectMarkers
    as_gray: boolean
    has_mark: true
    rotate_qr_pos?: RotateQrPos | null
    disable_whitebarance: boolean
  } = {
    image: base64Image,
    correct_image_size: correctImageSize,
    correct_markers: correctMarkers,
    as_gray: asGray,
    has_mark: true,
    rotate_qr_pos: rotateQrPos,
    disable_whitebarance: disableWhitebarance
  }

  if (!rotateQrPos) {
    delete params.rotate_qr_pos
  }

  const { data } = await axios.post('/api/v1/banshee/prediction', params)
  return data.data
}

/**
 * キャンバス要素を開放します
 * @param canvasEl キャンバス要素
 */
function disposeCanvas(canvasEl: HTMLCanvasElement): void {
  ImageConverter.dispose_canvas(canvasEl)
}

/**
 * Base64形式の画像をダウンロードします
 * @param base64Image Base64形式の画像
 * @param filename ファイル名
 */
function downloadBase64Image(base64Image: string, filename = 'image'): void {
  const a = document.createElement('a')
  a.download = filename
  a.href = `data:image/jpeg;base64,${base64Image}`
  a.click()
}

/**
 * image要素、canvas要素、またはファイルをBase64形式の画像のURLへ変換します
 * @param img image要素 / canvas要素 / ファイル
 * @returns Base64形式の画像のURL
 */
async function toBase64Url(
  img: HTMLImageElement | File | HTMLCanvasElement
): Promise<string> {
  return await ImageConverter.toBase64(img)
}

/**
 * Base64形式の画像を90度ずつ回転します
 * @param base64Image Base64形式の画像
 * @returns 回転後のBase64形式の画像
 */
async function rotate90(base64Image: string): Promise<string> {
  const canvasEl = await ImageConverter.toCanvas(base64Image)
  const image = await ImageConverter.toImage(base64Image)
  const ctx = canvasEl.getContext('2d')
  let cw = canvasEl.width
  let ch = canvasEl.height
  if (ctx) {
    canvasEl.width = ch
    canvasEl.height = cw
    cw = canvasEl.width
    ch = canvasEl.height

    ctx.save()
    ctx.translate(cw, ch / cw)
    ctx.rotate(Math.PI / 2)
    ctx.drawImage(image, 0, 0)
    ctx.restore()
  }
  return canvasToBase64(canvasEl)
}

/**
 * Base64形式の画像を180度ずつ回転します
 * @param base64Image Base64形式の画像
 * @returns 回転後のBase64形式の画像
 */
async function rotate180(base64Image: string): Promise<string> {
  const canvasEl = await ImageConverter.toCanvas(base64Image)
  const image = await ImageConverter.toImage(base64Image)
  const ctx = canvasEl.getContext('2d')
  const cw = canvasEl.width
  const ch = canvasEl.height
  if (ctx) {
    ctx.save()
    ctx.translate(cw, ch)
    ctx.rotate(Math.PI)
    ctx.drawImage(image, 0, 0)
    ctx.restore()
  }
  return canvasToBase64(canvasEl)
}

export default {
  base64UrlToBase64,
  canvasToBase64,
  clip,
  correctImage,
  disposeCanvas,
  downloadBase64Image,
  toBase64Url,
  rotate90,
  rotate180
}
